import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSparkCredsStatus, syncSparkCreds } from '../services/sparkCreds';
import { SPARK_CREDS_STATUS_BASE_KEY, SPARK_CREDS_STATUS_KEY } from './queryKeys';

/**
 * DB-only status (`live=false`) - cheap, so SparkCredsAction fires this on
 * mount unconditionally. `staleTime: 0` since a status card is exactly the
 * kind of "did it just change" surface that shouldn't show a minute-old
 * cached answer after a sync completes.
 */
export function useSparkCredsStatus(practiceId: string) {
    return useQuery({
        queryKey: SPARK_CREDS_STATUS_KEY(practiceId, false),
        queryFn: () => fetchSparkCredsStatus(practiceId, false),
        enabled: Boolean(practiceId),
        staleTime: 0,
        retry: false
    });
}

/**
 * The "Test" button's live check - makes the backend place a real call to
 * Spark with the stored token. A mutation rather than a second `useQuery`
 * (unlike useSparkCredsStatus above) because it must never auto-fire on
 * mount and every click should re-run it regardless of cache state.
 */
export function useTestSparkCreds(practiceId: string) {
    return useMutation({
        mutationFn: () => fetchSparkCredsStatus(practiceId, true)
    });
}

/**
 * Backs both the "Generate token"/"Refresh token" button (force: false) and
 * the force-regenerate confirm modal (force: true) - SparkCredsAction owns
 * one instance of this and passes its pending state down to the modal, so
 * only one sync can ever be in flight for a practice at a time.
 */
export function useSyncSparkCreds(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (force: boolean) => syncSparkCreds(practiceId, force),
        // onSettled, not onSuccess: a failed force-regenerate may have already
        // deleted the stored creds upstream, so the status card must refetch
        // after every attempt or it can show credentials that no longer exist.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: SPARK_CREDS_STATUS_BASE_KEY(practiceId) });
        }
    });
}
